// Two-month date range picker with preset shortcuts and Cancel/Apply.
// Matches the layout the user requested:
//   [Today/Yesterday/Last 7d/etc.]   [Apr cal]   [May cal]
//                                                       [Cancel]  [Apply]
//
// Pure React + Tailwind — no external date library.
// Dates are ISO strings (YYYY-MM-DD) on the outside; Date objects internally.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Icon } from './Icon'

// ---------- helpers (no deps) ----------

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December']
const DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

function iso(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
function fromIso(s: string | undefined | null): Date | null {
  if (!s) return null
  const [y, m, d] = s.split('-').map(Number)
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d)
}
function startOfDay(d: Date): Date { return new Date(d.getFullYear(), d.getMonth(), d.getDate()) }
function addDays(d: Date, n: number): Date {
  const c = startOfDay(d); c.setDate(c.getDate() + n); return c
}
function addMonths(d: Date, n: number): Date {
  const c = new Date(d.getFullYear(), d.getMonth() + n, 1); return c
}
function sameDay(a: Date | null, b: Date | null): boolean {
  return !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}
function inRange(d: Date, from: Date | null, to: Date | null): boolean {
  if (!from || !to) return false
  const t = startOfDay(d).getTime()
  return t >= startOfDay(from).getTime() && t <= startOfDay(to).getTime()
}
function fmt(d: Date | null): string {
  if (!d) return '—'
  return `${MONTHS[d.getMonth()].slice(0, 3)} ${d.getDate()}, ${d.getFullYear()}`
}

// Sunday-start month grid: returns 6 weeks × 7 days = 42 cells
function monthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1)
  const startOffset = first.getDay() // 0=Sun
  const start = addDays(first, -startOffset)
  return Array.from({ length: 42 }, (_, i) => addDays(start, i))
}

// ---------- presets ----------

type PresetKey =
  | 'today' | 'yesterday' | 'last_7' | 'this_week' | 'last_week'
  | 'last_30' | 'this_month' | 'last_month' | 'ytd' | 'lifetime'

interface Preset { key: PresetKey; label: string }
const PRESETS: Preset[] = [
  { key: 'today',      label: 'Today' },
  { key: 'yesterday',  label: 'Yesterday' },
  { key: 'last_7',     label: 'Last 7 days' },
  { key: 'this_week',  label: 'This week' },
  { key: 'last_week',  label: 'Last week' },
  { key: 'last_30',    label: 'Last 30 days' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'ytd',        label: 'Year to date' },
  { key: 'lifetime',   label: 'Lifetime' },
]

function rangeFromPreset(key: PresetKey, now: Date): [Date, Date] {
  const today = startOfDay(now)
  const sow = addDays(today, -today.getDay()) // Sunday start
  const som = new Date(today.getFullYear(), today.getMonth(), 1)
  switch (key) {
    case 'today':      return [today, today]
    case 'yesterday':  return [addDays(today, -1), addDays(today, -1)]
    case 'last_7':     return [addDays(today, -6), today]
    case 'this_week':  return [sow, today]
    case 'last_week':  return [addDays(sow, -7), addDays(sow, -1)]
    case 'last_30':    return [addDays(today, -29), today]
    case 'this_month': return [som, today]
    case 'last_month': {
      const lm  = addMonths(som, -1)
      const lmEnd = addDays(som, -1)
      return [lm, lmEnd]
    }
    case 'ytd':       return [new Date(today.getFullYear(), 0, 1), today]
    case 'lifetime':  return [new Date(2020, 0, 1), today]
  }
}

// ---------- component ----------

interface Props {
  value: { from: string; to: string }      // ISO YYYY-MM-DD; `to` is INCLUSIVE
  onChange: (next: { from: string; to: string }) => void
  /** Optional caption shown after the timezone line (e.g. browser tz) */
  timezone?: string
}

export function DateRangePicker({ value, onChange, timezone }: Props) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)

  // Working copy — committed only on Apply.
  const [draftFrom, setDraftFrom] = useState<Date | null>(fromIso(value.from))
  const [draftTo,   setDraftTo]   = useState<Date | null>(fromIso(value.to))
  const [hover,     setHover]     = useState<Date | null>(null)
  // Which two months to render. Default: month-of-from and month-after.
  const [anchor, setAnchor] = useState<Date>(() => {
    const f = fromIso(value.from) || new Date()
    return new Date(f.getFullYear(), f.getMonth(), 1)
  })

  // Open ⇒ reset draft to committed value
  useEffect(() => {
    if (open) {
      setDraftFrom(fromIso(value.from))
      setDraftTo(fromIso(value.to))
      setHover(null)
      const f = fromIso(value.from) || new Date()
      setAnchor(new Date(f.getFullYear(), f.getMonth(), 1))
    }
  }, [open, value.from, value.to])

  // Close on outside click (treated as cancel)
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const tz = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone

  // Click a day: first click sets from (clears to); second click sets to (auto-swap if before)
  function onDayClick(d: Date) {
    if (!draftFrom || (draftFrom && draftTo)) {
      setDraftFrom(d)
      setDraftTo(null)
      setHover(d)
    } else {
      if (d.getTime() < draftFrom.getTime()) {
        setDraftTo(draftFrom)
        setDraftFrom(d)
      } else {
        setDraftTo(d)
      }
    }
  }

  function applyPreset(key: PresetKey) {
    const [a, b] = rangeFromPreset(key, new Date())
    setDraftFrom(a); setDraftTo(b); setHover(null)
    setAnchor(new Date(a.getFullYear(), a.getMonth(), 1))
  }

  function commit() {
    if (!draftFrom) return
    const a = draftFrom
    const b = draftTo ?? draftFrom
    onChange({ from: iso(a), to: iso(b) })
    setOpen(false)
  }

  // Display string for the trigger button
  const triggerLabel = useMemo(() => {
    const f = fromIso(value.from)
    const t = fromIso(value.to)
    if (!f && !t) return 'Select date range'
    if (f && t && sameDay(f, t)) return fmt(f)
    return `${fmt(f)}  →  ${fmt(t)}`
  }, [value.from, value.to])

  // Range to highlight on the calendar (uses hover when no `to` yet)
  const hiFrom = draftFrom
  const hiTo   = draftTo ?? (draftFrom && hover && hover.getTime() >= draftFrom.getTime() ? hover : draftTo)

  const m1 = anchor
  const m2 = addMonths(anchor, 1)

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="h-9 px-3 inline-flex items-center gap-2 border border-ink-200 rounded-lg text-[13.5px] hover:bg-ink-50 bg-white min-w-[260px] text-left"
      >
        <Icon name="calendar-range" className="w-4 h-4 text-ink-500" />
        <span className={value.from && value.to ? '' : 'text-ink-400'}>{triggerLabel}</span>
        <Icon name="chevron-down" className="w-4 h-4 text-ink-400 ml-auto" />
      </button>

      {open && (
        <div
          className="absolute left-0 top-11 z-50 card shadow-lg p-0 overflow-hidden"
          style={{ minWidth: 700 }}
        >
          <div className="flex">
            {/* Preset shortcuts */}
            <div className="w-[140px] border-r border-ink-100 py-2">
              {PRESETS.map(p => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => applyPreset(p.key)}
                  className="w-full text-left px-4 py-1.5 text-[13px] text-ink-700 hover:bg-ink-50 hover:text-royal-600"
                >
                  {p.label}
                </button>
              ))}
              <div className="px-4 pt-3 text-[10px] text-ink-400 uppercase tracking-wider">
                Timezone
              </div>
              <div className="px-4 pb-2 text-[12px] text-ink-600">{tz}</div>
            </div>

            {/* Calendars */}
            <div className="flex-1 p-4">
              <div className="flex">
                <CalendarMonth
                  date={m1}
                  showLeftNav
                  onPrev={() => setAnchor(addMonths(anchor, -1))}
                  onNext={() => setAnchor(addMonths(anchor, 1))}
                  hiFrom={hiFrom}
                  hiTo={hiTo}
                  hover={hover}
                  draftFrom={draftFrom}
                  draftTo={draftTo}
                  onDayClick={onDayClick}
                  onDayHover={setHover}
                />
                <div className="w-4" />
                <CalendarMonth
                  date={m2}
                  showRightNav
                  onPrev={() => setAnchor(addMonths(anchor, -1))}
                  onNext={() => setAnchor(addMonths(anchor, 1))}
                  hiFrom={hiFrom}
                  hiTo={hiTo}
                  hover={hover}
                  draftFrom={draftFrom}
                  draftTo={draftTo}
                  onDayClick={onDayClick}
                  onDayHover={setHover}
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 mt-3 border-t border-ink-100">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-[13px] text-royal-600 font-medium px-2 py-1.5 hover:underline"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={commit}
                  disabled={!draftFrom}
                  className="h-8 px-4 rounded-md bg-ink-900 text-white text-[13px] font-semibold hover:bg-ink-700 disabled:opacity-40"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------- single month grid ----------

interface MonthProps {
  date: Date                // anchor (any day in the month)
  showLeftNav?: boolean
  showRightNav?: boolean
  onPrev: () => void
  onNext: () => void
  hiFrom: Date | null
  hiTo: Date | null
  hover: Date | null
  draftFrom: Date | null
  draftTo: Date | null
  onDayClick: (d: Date) => void
  onDayHover: (d: Date | null) => void
}

function CalendarMonth(p: MonthProps) {
  const y = p.date.getFullYear()
  const m = p.date.getMonth()
  const cells = useMemo(() => monthGrid(y, m), [y, m])
  const today = startOfDay(new Date())

  return (
    <div className="flex-1" style={{ minWidth: 232 }}>
      <div className="flex items-center justify-between px-1 mb-2">
        {p.showLeftNav ? (
          <button type="button" onClick={p.onPrev} className="w-7 h-7 grid place-items-center rounded hover:bg-ink-100 text-ink-600">
            <Icon name="chevron-left" className="w-4 h-4" />
          </button>
        ) : <span className="w-7" />}
        <div className="text-[13px] font-semibold text-ink-800">
          {MONTHS[m]} <span className="text-ink-500 font-normal">{y}</span>
        </div>
        {p.showRightNav ? (
          <button type="button" onClick={p.onNext} className="w-7 h-7 grid place-items-center rounded hover:bg-ink-100 text-ink-600">
            <Icon name="chevron-right" className="w-4 h-4" />
          </button>
        ) : <span className="w-7" />}
      </div>
      <div className="grid grid-cols-7 gap-0 text-[11px] text-ink-400 mb-1">
        {DOW.map((d) => <div key={d} className="h-6 grid place-items-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0">
        {cells.map((d, i) => {
          const inMonth = d.getMonth() === m
          const isStart = sameDay(d, p.draftFrom)
          const isEnd   = sameDay(d, p.draftTo)
          const inSel   = inRange(d, p.hiFrom, p.hiTo)
          const isToday = sameDay(d, today)

          let bg = ''
          let txt = inMonth ? 'text-ink-800' : 'text-ink-300'
          if (inSel)  { bg = 'bg-ink-100'; txt = 'text-ink-800' }
          if (isStart || isEnd) { bg = 'bg-royal-500'; txt = 'text-white font-semibold' }
          if (isToday && !(isStart || isEnd)) { txt += ' ring-1 ring-royal-300 rounded-full' }

          return (
            <button
              key={i}
              type="button"
              onClick={() => p.onDayClick(d)}
              onMouseEnter={() => p.onDayHover(d)}
              className={`h-8 w-8 text-[12.5px] grid place-items-center mx-auto rounded-full ${bg} ${txt} hover:bg-ink-100`}
            >
              {d.getDate()}
            </button>
          )
        })}
      </div>
    </div>
  )
}
